// Story 1.18 — TanStack Query mutation for POST /api/v1/wallet/refund-request.
//
// Per-submit idempotency key (regenerated per submit, NOT per modal
// mount — same pattern as Stories 1.16 + 1.17).
//
// On success invalidates wallet-balance + wallet-history +
// refund-eligible-topups so the debit + reduced refundable amount show
// up immediately on the next render.

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type RefundRequestBody = {
  ledger_entry_id: string;
  amount_cents: number;
};

export type RefundRequestResponse = {
  refund_request_id: string;
  status: string;
  amount_cents: number;
  expected_arrival: string | null;
  card_last4: string;
};

export type RefundError = {
  code: string;
  message: string;
  field?: string;
  // 422 REFUND_INELIGIBLE carries reason + refundable_amount_cents.
  reason?:
    | "top_up_not_found"
    | "not_a_top_up"
    | "window_expired"
    | "amount_exceeds_refundable"
    | "already_fully_refunded";
  refundable_amount_cents?: number;
};

async function postRefundRequest(
  body: RefundRequestBody,
  idempotencyKey: string,
): Promise<RefundRequestResponse> {
  const response = await fetch("/api/v1/wallet/refund-request", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": idempotencyKey,
    },
    body: JSON.stringify(body),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: RefundError = {
      code: "REFUND_FAILED",
      message: `Refund failed (HTTP ${response.status})`,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = { ...raw.error };
      }
    } catch {
      // Body not JSON.
    }
    throw detail;
  }
  return (await response.json()) as RefundRequestResponse;
}

export function useRefundRequest() {
  const queryClient = useQueryClient();
  return useMutation<
    RefundRequestResponse,
    RefundError,
    { body: RefundRequestBody; idempotencyKey: string }
  >({
    mutationFn: ({ body, idempotencyKey }) =>
      postRefundRequest(body, idempotencyKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["wallet-balance"] });
      queryClient.invalidateQueries({ queryKey: ["wallet-history"] });
      queryClient.invalidateQueries({ queryKey: ["refund-eligible-topups"] });
    },
  });
}
